import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiMenu, FiX } from 'react-icons/fi';
import { FaFacebookF } from 'react-icons/fa';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: ${props => props.$scrolled ? '12px 0' : '20px 0'};
  transition: all 0.4s ease;
  background: ${props => props.$scrolled ? 'rgba(60, 30, 20, 0.95)' : 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 70%, transparent 100%)'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const LogoImg = styled.img`
  width: 40px;
  height: 40px;
  transition: all 0.4s ease;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
`;

const LogoText = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--wheat);
  transition: all 0.4s ease;
  text-shadow: ${props => props.$scrolled ? 'none' : '0 2px 10px rgba(0,0,0,0.3)'};

  span {
    display: block;
    font-size: 0.7rem;
    font-family: 'Quicksand', sans-serif;
    font-weight: 500;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: var(--wheat);
    opacity: 0.8;
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 35px;

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 280px;
    background: rgba(60, 30, 20, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: center;
    gap: 30px;
    transform: ${props => props.$open ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.4s ease;
  }
`;

const NavLink = styled.a`
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--wheat);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  text-shadow: ${props => props.$scrolled ? 'none' : '0 1px 4px rgba(0,0,0,0.3)'};

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--wheat);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 900px) {
    font-size: 1.1rem;
    text-shadow: none;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(245, 222, 179, 0.15);
  border: 1px solid rgba(245, 222, 179, 0.3);
  color: var(--wheat);
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: var(--barn-red);
    border-color: var(--barn-red);
    transform: scale(1.1);
  }

  @media (max-width: 900px) {
    background: rgba(245, 222, 179, 0.15);
  }
`;

const MobileBtn = styled.button`
  display: none;
  background: none;
  color: var(--wheat);
  z-index: 1001;
  filter: drop-shadow(0 1px 3px rgba(0,0,0,0.3));

  @media (max-width: 900px) {
    display: block;
  }
`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <Nav $scrolled={scrolled}>
      <Container>
        <Logo onClick={() => scrollTo('hero')}>
          <LogoImg src={process.env.PUBLIC_URL + '/logo.svg'} alt="Red Barn Gardens" />
          <LogoText $scrolled={scrolled}>
            Red Barn
            <span>Gardens</span>
          </LogoText>
        </Logo>

        <Links $open={menuOpen}>
          <NavLink $scrolled={scrolled} onClick={() => scrollTo('greenhouse')}>Greenhouse</NavLink>
          <NavLink $scrolled={scrolled} onClick={() => scrollTo('events')}>Events</NavLink>
          <NavLink $scrolled={scrolled} onClick={() => scrollTo('our-story')}>Our Story</NavLink>
          <NavLink $scrolled={scrolled} onClick={() => scrollTo('contact')}>Contact</NavLink>
          <NavLink $scrolled={scrolled} onClick={() => scrollTo('greeter')}>Meet Barney</NavLink>
          <SocialLink
            href="https://www.facebook.com/RedBarnGardens"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </SocialLink>
        </Links>

        <MobileBtn onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
        </MobileBtn>
      </Container>
    </Nav>
  );
}
